import { Theme } from '@/utils/constants';
import { formatText } from '@/utils/helpers';
import ChevronLeftIcon from '@material-ui/icons/ChevronLeft';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import clsx from 'clsx';
import React, { createRef, useEffect, useRef } from 'react';
import Slider from 'react-slick';
import Carousel from '../Carousel';
import Image from '../Image';
import { MuiIconButton, MuiTypography } from '../material-ui';
import { ImageCarouselProps } from './ImageCarousel.interface';
import useStyles from './ImageCarousel.style';

const PrevButton = (props: any) => {
  const classes = useStyles({});
  const { onClick, className: btnClassName, inline } = props;
  return (
    <MuiIconButton
      aria-label="Preview Image"
      onClick={onClick}
      className={clsx(classes.navArrow, classes.prevArrow, btnClassName, 'gtm-scroll')}
    >
      <ChevronLeftIcon color="inherit" fontSize={inline ? 'default' : 'large'} />
    </MuiIconButton>
  );
};

const NextButton = (props: any) => {
  const classes = useStyles({});
  const { onClick, className: btnClassName, inline } = props;
  return (
    <MuiIconButton
      aria-label="Next Image"
      onClick={onClick}
      className={clsx(classes.navArrow, classes.nextArrow, btnClassName, 'gtm-scroll')}
    >
      <ChevronRightIcon color="inherit" fontSize={inline ? 'default' : 'large'} />
    </MuiIconButton>
  );
};

const ImageCarousel = ({
  content,
  index,
  syncNav,
  inline,
  pagination = true,
  arrows = true,
  theme = Theme.light,
  click,
  className: overrideClassName,
  type = 'base',
  base = 'small',
  size,
  labels,
}: ImageCarouselProps) => {
  const classes = useStyles({});
  const list = content && content.contentList;

  const sliderRef = createRef<Slider>();

  useEffect(() => {
    sliderRef?.current?.slickGoTo(index || 0);
  }, [index, sliderRef]);

  const settingsMain = React.useMemo(
    () => ({
      arrows,
      dots: false,
      fade: true,
      speed: 500,
      slidesToShow: 1,
      slidesToScroll: 1,
      rows: 1,
      initialSlide: index,
      prevArrow: <PrevButton inline={inline} />,
      nextArrow: <NextButton inline={inline} />,
      useCSS: true,
    }),
    [arrows, index, inline],
  );

  const rootClassName = clsx(
    classes.root,
    `${theme}-theme`,
    inline && 'is-inline',
    overrideClassName,
  );

  return (
    <div className={rootClassName}>
      <Carousel ref={sliderRef} settings={settingsMain} onChange={syncNav} theme={theme}>
        {list &&
          list
            .filter((item: any) => Boolean(item.image))
            .map((item: any, i) => {
              return (
                <div
                  key={`image_${index}`}
                  role="button"
                  tabIndex={0}
                  className={classes.focusImage}
                  onClick={click}
                  onKeyPress={click}
                >
                  <figure>
                    <Image image={item.image} type={type} base={base} size={size} />
                  </figure>
                  <div className={classes.caption}>
                    <MuiTypography
                      variant={inline ? 'caption' : 'overline'}
                      component="p"
                      data-testid="qa-image-title"
                      className={clsx(classes.title, 'gtm-image-name')}
                    >
                      {item.image.title}
                    </MuiTypography>
                    {pagination && (
                      <MuiTypography
                        variant="body2"
                        component="p"
                        data-testid="qa-image-index"
                        className={classes.pagination}
                      >
                        {formatText(labels?.image_of || 'Image %1 of %2', [
                          `${i + 1}`,
                          `${list.length}`,
                        ])}
                      </MuiTypography>
                    )}
                  </div>
                </div>
              );
            })}
      </Carousel>
    </div>
  );
};

export default ImageCarousel;
